import React, { useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { createRoot } from '@wordpress/element';
import { DateGrid } from "./components/forms/dateGrid";
import { displayErrorMessage, displayLoader, ConnectionContext, getConnectionInfo } from './utils';
import { getStoredData } from "./utils/data-pulls";
import { Sidebar } from "./components";
import { LiveGamesWidget } from "./components/widgets/liveGamesWidget";
import { UpcomingGamesWidget } from "./components/widgets/upcomingGamesWidget";
import { SmallMatchBox } from "./components/smallMatchBox";


const RecentMatchesApp = (props) => {
    const { 
        ...restProps 
    } = props;

    // Error and load handling
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isMatchesLoaded, setIsMatchesLoaded] = useState(false);

    // Connection Information
    const [connectionInfo, setConnectionInfo] = useState(null);

    const [ matches, setMatches ] = useState(null);
    const [ scheduleDates, setScheduleDates ] = useState(null);
    const [ selectedScheduleDate, setSelectedScheduleDate ] = useState(null);


    /**
     * Get our connection info
     */
    useEffect(() => {

        // Get our connection info
        getConnectionInfo().then(
            (result) => {
                setConnectionInfo(result.data);
            }
        ).catch(
            (error) => {
                setError(error);
            }
        );

      }, []);


    /**
     * Get the schedule dates for the current season
     */
    useEffect(() => {
        setIsLoaded(false);

        let args = [
            {
                key: 'scheduleDates',
                type: 'recent'
            }
        ];

        // Get our stored data
        getStoredData(args).then(
            (result) => {

                // Set schedule
                if ( result.data.scheduleDates ) {
                    setScheduleDates(result.data.scheduleDates);
                }

                // Set loaded
                setIsLoaded(true);

            }
        ).catch(
            (error) => {
                setError(error);
            }
        );

    }, []);


    /**
     * Get matches for the specific day
     */
    useEffect(() => {
        if ( !selectedScheduleDate ) return;
        setIsMatchesLoaded(false);

        // Turn date into something parseable by PHP
        const year = selectedScheduleDate.toLocaleString("default", { year: "numeric", timeZone: 'UTC' });
        const month = selectedScheduleDate.toLocaleString("default", { month: "2-digit", timeZone: 'UTC' });
        const day = selectedScheduleDate.toLocaleString("default", { day: "2-digit", timeZone: 'UTC' });
        const formattedDate = year + "-" + month + "-" + day;

        let args = [
            {
                key: 'matches',
                type: 'recent',
                date: formattedDate,
                season: 'all',
                competition: 'all'
            }
        ];

        // // Get our stored data
        getStoredData(args).then(
            (result) => {

                // Set Matches
                if ( result.data.matches ) {

                    /**
                     * Sort the matches into associative arrays by competition
                     */
                    let sortedMatches = {};
                    result.data.matches.forEach((match, i) => {
                        const competition = match.competition;

                        // Create the competition in the array if it doesn't already exist
                        if ( !sortedMatches.hasOwnProperty(competition.id) ) {
                            sortedMatches[competition.id] = {
                                name: competition.name,
                                matches: []
                            };
                        }

                        // Add the match to its competition
                        sortedMatches[competition.id].matches.push(match);

                    });

                    if ( result.data.matches.length == 0 ) {
                        setMatches(null);
                    } else {
                        setMatches(sortedMatches);
                    }

                }

                setIsMatchesLoaded(true);

            }
        ).catch(
            (error) => {
                setError(error);
            }
        );

    }, [selectedScheduleDate]);


    // Change the selected date
    const handleDateChange = (date = null) => {
        if ( !date ) return;
        setSelectedScheduleDate(date);
    }


    // Bail if we don't have data yet
    if (!isLoaded ) {
        return displayLoader('acaLoader--main');
    }

    // Display an error message if there's a problem
    if ( error ) {
        console.log(error);
        return displayErrorMessage();
    }

    return (
        <ConnectionContext.Provider value={connectionInfo}>
            <DateGrid dates={scheduleDates} onChange={handleDateChange}></DateGrid>

            <section className="acaPage acaPage--withSidebar acaPage--recentMatches">
                
                <div className="acaMain">
                    { isMatchesLoaded ? 
                        selectedScheduleDate && matches ? (
                            <>
                                <h2 className="acaH1 acaScheduleGrid__title">{ selectedScheduleDate.toLocaleString("en-US", { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }) }</h2>

                                {
                                Object.keys(matches).map((key, i) => (
                                        <div key={i} className="acaScheduleGrid__section">
                                            <h3 className="acaH2 acaScheduleGrid__subtitle">{ matches[key].name }</h3>
                                            <div className="acaScheduleGrid acaScheduleGrid--2Col">
                                                { matches[key].matches.map((match, n) => (
                                                    <SmallMatchBox key={n} data={match} />
                                                )) }
                                            </div>
                                        </div>
                                    ) )
                                }

                            </>
                        ) : (
                            displayErrorMessage('No recent matches to show. Check back later, or see the schedule and standings page for upcoming games.')
                        ) 
                    : displayLoader('acaLoader--main') }
                </div>

                <Sidebar soccerImg>
                    <ErrorBoundary fallback={displayErrorMessage()}>
                        <LiveGamesWidget />
                    </ErrorBoundary>
                    <ErrorBoundary fallback={displayErrorMessage()}>
                        <UpcomingGamesWidget />
                    </ErrorBoundary>
                </Sidebar>

            </section>
        </ConnectionContext.Provider>
    );
}

export default RecentMatchesApp;

// Render Match Page
const appContainer = document.getElementById('recentMatchesApp');
const root = createRoot(appContainer);
root.render(<RecentMatchesApp />);